import { useState, useEffect, useCallback } from 'react';
import izenuAPI from '../../services/izenuAPI';
import { Link } from 'react-router-dom';

/**
 * Release sales. In the distributor panel: its own artists. With `admin`
 * (admin panel → Financeiro): every artist, with a distributor filter and
 * column. Same layout as the admin's Royalties page.
 *
 * Only release_purchases with money count (ReleasePurchase::IS_SALE): the old
 * album store (album_payments) left the reports on 2026-10-13.
 */

const STATUS_LABELS = { completed: 'Concluída', pending: 'Pendente', failed: 'Falhou', refunded: 'Reembolsada' };
const STATUS_CLS = {
    completed: 'bg-green-50 text-green-700',
    pending: 'bg-amber-50 text-amber-700',
    failed: 'bg-red-50 text-red-600',
    refunded: 'bg-gray-100 text-gray-600',
};
const METHOD_LABELS = {
    multicaixa: 'Multicaixa Express', appy_pay: 'Multicaixa Express', stripe: 'Cartão (Stripe)',
    apple_iap: 'Apple', apple_pay: 'Apple', transference: 'Transferência',
};

const kz = (v) => `${Number(v || 0).toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kz`;
const money = (v, currency) => (currency === 'USD'
    ? new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'USD' }).format(Number(v || 0))
    : kz(v));
const dateTime = (s) => {
    if (!s) return '—';
    const d = new Date(String(s).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? s : d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// CSV that opens straight in Excel in Portuguese: ";" separator, decimal comma, UTF-8 BOM.
function toCSV(rows, headers) {
    const cell = (v) => {
        if (v == null) return '';
        const s = typeof v === 'number' ? String(Math.round(v * 100) / 100).replace('.', ',') : String(v);
        return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = headers.map((h) => cell(h.label)).join(';');
    const body = rows.map((r) => headers.map((h) => cell(h.value(r))).join(';')).join('\n');
    return '﻿' + head + '\n' + body;
}

function download(content, filename) {
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function Section({ title, subtitle, action, children }) {
    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function Stat({ label, value, hint, tone }) {
    return (
        <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">{label}</div>
            <div className={`text-xl font-semibold tabular-nums ${tone || 'text-gray-900'}`}>{value}</div>
            {hint && <div className="text-xs text-gray-400">{hint}</div>}
        </div>
    );
}


export default function SalesReport({ admin = false }) {
    // Each panel keeps its colour: indigo in the admin, amber for distributors.
    const accent = admin
        ? { ring: 'focus:border-indigo-500 focus:ring-indigo-500', soft: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100', hover: 'hover:bg-indigo-50', link: 'text-indigo-700 hover:underline' }
        : { ring: 'focus:border-amber-500 focus:ring-amber-500', soft: 'bg-amber-50 text-amber-800 hover:bg-amber-100', hover: 'hover:bg-amber-50', link: 'text-amber-700 hover:underline' };
    const inputCls = `block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 ${accent.ring}`;

    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [byItem, setByItem] = useState([]);
    const [byBuyer, setByBuyer] = useState([]);
    const [status, setStatus] = useState('completed');
    const [distributors, setDistributors] = useState([]);
    const [distributorId, setDistributorId] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [artists, setArtists] = useState([]);
    const [artistInput, setArtistInput] = useState('');
    const [artistId, setArtistId] = useState('');
    const [showArtists, setShowArtists] = useState(false);

    useEffect(() => {
        izenuAPI.get(admin ? '/admin/sales/artists.php' : '/distributor/reports/artists.php')
            .then(({ data }) => {
                if (!data.response) return;
                setArtists(data.artists || []);
                setDistributors(data.distributors || []);
            })
            .catch(() => {});
    }, [admin]);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (status) params.status = status;
            if (admin && distributorId) params.distributor_id = distributorId;
            if (artistId) params.artist_id = artistId;
            if (from) params.from = from;
            if (to) params.to = to;
            const { data } = await izenuAPI.get(admin ? '/admin/sales/sales.php' : '/distributor/reports/sales.php', { params });
            if (data.response) {
                setSummary(data.summary);
                // An item with nothing sold or pending under the current filter only adds a row of zeros.
                setByItem((data.by_item || []).filter((r) => status === 'failed' || status === 'refunded' || Number(r.completed_qty) > 0 || Number(r.pending_qty) > 0));
                setByBuyer(data.by_buyer || []);
            }
        } catch {
            // keeps the previous figures
        }
        setLoading(false);
    }, [admin, status, distributorId, artistId, from, to]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const filteredArtists = artistInput
        ? artists.filter((a) => (a.display || '').toLowerCase().includes(artistInput.toLowerCase()))
        : artists;
    const clearFilters = () => { setStatus('completed'); setDistributorId(''); setFrom(''); setTo(''); setArtistId(''); setArtistInput(''); };
    const today = new Date().toISOString().slice(0, 10);
    const distCol = admin ? [{ label: 'Distribuidora', value: (r) => r.distributor || 'Sem distribuidora' }] : [];

    const exportItems = () => download(toCSV(byItem, [
        { label: 'ID', value: (r) => r.item_id },
        { label: 'Lançamento', value: (r) => r.title },
        { label: 'Artista', value: (r) => r.artist },
        ...distCol,
        { label: 'Vendas concluídas', value: (r) => Number(r.completed_qty || 0) },
        { label: 'Receita (Kz)', value: (r) => Number(r.completed_revenue || 0) },
        { label: 'Parte dos artistas (Kz)', value: (r) => Number(r.artist_share_aoa || 0) },
        { label: 'Pendentes', value: (r) => Number(r.pending_qty || 0) },
        { label: 'Receita pendente (Kz)', value: (r) => Number(r.pending_revenue || 0) },
        { label: 'Última compra', value: (r) => r.last_at },
    ]), `vendas_por_lancamento_${today}.csv`);

    const exportBuyers = () => download(toCSV(byBuyer, [
        { label: 'Data', value: (r) => r.created_at },
        { label: 'Lançamento', value: (r) => r.item_title },
        { label: 'Artista', value: (r) => r.artist },
        ...distCol,
        { label: 'Comprador', value: (r) => r.buyer_name },
        { label: 'Email', value: (r) => r.buyer_email },
        { label: 'Montante', value: (r) => Number(r.amount || 0) },
        { label: 'Moeda', value: (r) => r.currency },
        { label: 'Montante (Kz)', value: (r) => Number(r.amount_aoa || r.amount || 0) },
        { label: 'Parte do artista', value: (r) => (r.artist_amount != null && r.status === 'completed' ? Number(r.artist_amount) : '') },
        { label: 'Método', value: (r) => METHOD_LABELS[r.payment_method] || r.payment_method },
        { label: 'Estado', value: (r) => STATUS_LABELS[r.status] || r.status },
    ]), `vendas_por_comprador_${today}.csv`);

    const label = (t) => <span className="mb-1 block text-sm font-medium text-gray-700">{t}</span>;
    const csvBtn = (onClick, disabled) => (
        <button type="button" onClick={onClick} disabled={disabled}
            className={`rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-40 ${accent.soft}`}>Descarregar CSV</button>
    );

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-3 text-left sm:p-6">
            <div>
                <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Vendas de álbuns</h1>
                <p className="text-sm text-gray-500">
                    {admin ? 'Vendas de álbuns, EP e singles na loja, de todos os artistas.' : 'Só as compras de álbuns, EP e singles dos artistas que geris.'}
                    {' '}Preço pago pelo fã, convertido para Kz ao câmbio actual; a parte dos artistas é {Number(summary?.share_pct || 70)}% de cada venda (desde 17/10/2026).
                </p>
                <p className="mt-1 text-sm text-gray-500">
                    Os royalties de streaming estão em <Link to="/royalties" className={`font-semibold ${accent.link}`}>Royalties de streaming</Link>; o total a pagar, somando os dois, em{' '}
                    <Link to={admin ? '/artist-payouts' : '/payouts'} className={`font-semibold ${accent.link}`}>{admin ? 'Pagamentos a artistas' : 'Pagamentos'}</Link>.
                </p>
            </div>

            <Section title="Filtros">
                <div className={`grid grid-cols-1 gap-4 ${admin ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
                    <label className="block">{label('Estado')}
                        <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">Todos</option>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select></label>
                    {admin && (
                        <label className="block">{label('Distribuidora')}
                            <select className={inputCls} value={distributorId} onChange={(e) => setDistributorId(e.target.value)}>
                                <option value="">Todas</option>
                                {distributors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                <option value="none">Sem distribuidora</option>
                            </select></label>
                    )}
                    <div className="relative">{label('Artista')}
                        <input className={inputCls} value={artistInput} placeholder="Todos"
                            onChange={(e) => { setArtistInput(e.target.value); setArtistId(''); setShowArtists(true); }}
                            onFocus={() => setShowArtists(true)} onBlur={() => setTimeout(() => setShowArtists(false), 150)} />
                        {showArtists && filteredArtists.length > 0 && (
                            <ul className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg !border !border-solid border-gray-200 bg-white shadow-lg [&_li]:!p-0">
                                {filteredArtists.slice(0, 50).map((a) => (
                                    <li key={a.artist_id}>
                                        <button type="button" onMouseDown={() => { setArtistId(a.artist_id); setArtistInput(a.display); setShowArtists(false); }}
                                            className={`w-full px-3 py-2 text-left text-sm text-gray-900 ${accent.hover}`}>{a.display}</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <label className="block">{label('De')}
                        <input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                    <label className="block">{label('Até')}
                        <input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} /></label>
                </div>
                <div className="mt-3 flex justify-end">
                    <button type="button" onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-800 hover:underline">Limpar filtros</button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                    <Stat label="Receita" value={summary ? kz(summary.total_revenue) : '—'} hint="pago pelos fãs" tone="text-green-700" />
                    <Stat label={`Parte dos artistas (${Number(summary?.share_pct || 70)}%)`} value={summary ? kz(summary.artist_share) : '—'} hint="das vendas concluídas" />
                    <Stat label="Vendas concluídas" value={summary ? summary.completed_qty : '—'} />
                    <Stat label="Ticket médio" value={summary ? kz(summary.avg_ticket) : '—'} />
                    <Stat label="Pendentes" value={summary ? summary.pending_qty : '—'} hint={summary ? kz(summary.pending_revenue) : ''} tone="text-amber-700" />
                </div>
            </Section>

            <Section title="Por lançamento" subtitle="Cada lançamento com vendas no período." action={csvBtn(exportItems, !byItem.length)}>
                {loading ? <p className="text-sm text-gray-500">A carregar…</p>
                    : byItem.length === 0 ? <p className="text-sm text-gray-500">Sem vendas com estes filtros.</p> : (
                        <ul className="divide-y divide-gray-100 rounded-lg !border !border-solid border-gray-200 [&>li]:!p-0">
                            {byItem.map((r) => (
                                <li key={`${r.type}-${r.item_id}`}>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                                        <span className="w-14 shrink-0 text-xs text-gray-400">#{r.item_id}</span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-medium text-gray-900">{r.title}</span>
                                            <span className="block truncate text-xs text-gray-500">{r.artist}{admin ? ` · ${r.distributor || 'Sem distribuidora'}` : ''}</span>
                                        </span>
                                        <span className="text-right">
                                            <span className="block font-semibold tabular-nums text-gray-900">{kz(r.completed_revenue)}</span>
                                            {Number(r.artist_share_aoa) > 0 && <span className="block text-xs tabular-nums text-gray-600">artista: {kz(r.artist_share_aoa)}</span>}
                                            <span className="block text-xs text-gray-500">
                                                {r.completed_qty || 0} {Number(r.completed_qty) === 1 ? 'venda' : 'vendas'}
                                                {Number(r.pending_qty) > 0 && <span className="text-amber-700"> · {r.pending_qty} pendente(s)</span>}
                                            </span>
                                        </span>
                                        <span className="w-32 text-right text-xs text-gray-400">{dateTime(r.last_at)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
            </Section>

            <Section title="Compras" subtitle="Cada compra individual, a mais recente primeiro." action={csvBtn(exportBuyers, !byBuyer.length)}>
                {loading ? <p className="text-sm text-gray-500">A carregar…</p>
                    : byBuyer.length === 0 ? <p className="text-sm text-gray-500">Sem compras com estes filtros.</p> : (
                        <ul className="divide-y divide-gray-100 rounded-lg !border !border-solid border-gray-200 [&>li]:!p-0">
                            {byBuyer.map((r) => (
                                <li key={`${r.type}-${r.purchase_id}`}>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                                        <span className="w-32 shrink-0 text-xs text-gray-500">{dateTime(r.created_at)}</span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-medium text-gray-900">{r.item_title}</span>
                                            <span className="block truncate text-xs text-gray-500">{r.artist}{admin ? ` · ${r.distributor || 'Sem distribuidora'}` : ''}</span>
                                        </span>
                                        <span className="min-w-0 basis-48">
                                            <span className="block truncate text-gray-900">{r.buyer_name || '—'}</span>
                                            <span className="block truncate text-xs text-gray-500">{r.buyer_email || ''}</span>
                                        </span>
                                        <span className="w-40 whitespace-nowrap text-right">
                                            <span className="block tabular-nums text-gray-900">{money(r.amount, r.currency)}</span>
                                            {r.currency && r.currency !== 'AOA' && <span className="block text-xs text-gray-400">≈ {kz(r.amount_aoa)}</span>}
                                            {r.artist_amount != null && r.status === 'completed' && <span className="block text-xs tabular-nums text-gray-600">artista: {money(r.artist_amount, r.currency)}</span>}
                                        </span>
                                        <span className="w-32 text-xs text-gray-500">{METHOD_LABELS[r.payment_method] || r.payment_method}</span>
                                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[r.status] || 'bg-gray-100 text-gray-600'}`}>
                                            {STATUS_LABELS[r.status] || r.status}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
            </Section>
        </div>
    );
}
